import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIndustryStore } from '../stores/industryStore';
import { Industry } from '../types/industry';

export const useIndustrySelector = (profileIndustries: Industry[] = []) => {
  const { t } = useTranslation('common');
  const { industries, loading, fetchIndustries } = useIndustryStore();

  const options = industries
    .map(industry => ({
      id: industry.id,
      label: t(`industries.${industry.slug}`, { defaultValue: industry.label }),
      slug: industry.slug,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [selectedOptions, setSelectedOptions] = useState<Industry[]>([]);
  const [searchInput, setSearchInput] = useState('');

  // Once the initial profileIndustries selection has been applied, stop
  // re-applying it. Without this guard, clearing the selection to pick new
  // industries (selectedOptions.length hits 0) re-triggers the effect below,
  // which immediately restores the original industries from
  // profileIndustries — making it impossible to ever change the selection on
  // an edit form where industries are pre-filled.
  const hasHydratedRef = useRef(selectedOptions.length > 0);

  useEffect(() => {
    if (!industries.length && !loading) {
      fetchIndustries();
    }
  }, [fetchIndustries, industries.length, loading]);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (!selectedOptions.length) {
      const initialSelectedOptions = options.filter(opt =>
        profileIndustries?.some((s: Industry) => s.id === opt.id)
      );
      if (initialSelectedOptions.length) {
        setSelectedOptions(initialSelectedOptions);
        hasHydratedRef.current = true;
      }
    }
  }, [profileIndustries, options, selectedOptions.length]);

  const filteredOptions = useMemo(() => {
    if (!searchInput.trim()) return options;
    const lower = searchInput.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(lower));
  }, [options, searchInput]);

  return {
    options,
    filteredOptions,
    selectedOptions,
    setSelectedOptions,
    searchInput,
    setSearchInput,
    loading,
  };
};
